import React, { useContext, useRef, useState, useEffect, useLayoutEffect, useMemo } from "react";
import Select, { components } from "react-select";
import AppContext from "../../context/AppContext";
import { isRTL } from "../../utils/rtl";
import { Search, Check } from "lucide-react";
import translations from "./Translations";
import CommonTooltip from "./CommonTooltip";

const getSafeMenuPortalTarget = (target) => {
    if (!target) return undefined;
    if (typeof document === "undefined" || typeof Element === "undefined") {
        return undefined;
    }
    if (target instanceof Element && target.isConnected) {
        return target;
    }
    if (document.body instanceof Element && document.body.isConnected) {
        return document.body;
    }
    return undefined;
};

const getScrollParent = (element) => {
    if (typeof window === "undefined" || !element) return null;
    const modalScrollParent = element.closest?.(".user-management-modal-scroll");
    if (modalScrollParent) return modalScrollParent;

    let parent = element.parentElement;
    while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        if (/(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return document.scrollingElement || document.documentElement;
};

// The nearest ancestor that actually clips the menu vertically. Unlike getScrollParent
// this ignores whether the element is currently scrolled, because an unscrolled
// overflow-auto modal body still bounds where the menu can usefully appear.
const getClipParent = (element) => {
    if (typeof window === "undefined" || !element) return null;
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
        const { overflowY } = window.getComputedStyle(parent);
        if (/(auto|scroll|overlay|hidden)/.test(overflowY)) return parent;
        parent = parent.parentElement;
    }
    return null;
};

const toPixelHeight = (value, fallback = 250) => {
    if (typeof value === "number") return value;
    const parsed = Number.parseInt(String(value), 10);
    return Number.isNaN(parsed) ? fallback : parsed;
};

// CustomMenuList stacks a search row above the option list, so the menu renders this
// much taller than maxMenuHeight (which only caps the list).
const MENU_SEARCH_ROW_HEIGHT = 48;
// One option row (8px padding top/bottom around a ~22px line) plus the menu list's own
// padding, border and the 4px gap it sits off the control.
const OPTION_ROW_HEIGHT = 38;
const MENU_CHROME_HEIGHT = 18;

// How tall the menu will actually render. maxMenuHeight only caps the list, so measuring
// a three-option menu against the cap would flip it to the top for space it never needed.
const getEstimatedMenuHeight = (optionCount, maxTotalHeight, searchRowHeight = MENU_SEARCH_ROW_HEIGHT) => {
    const maxListHeight = maxTotalHeight - searchRowHeight;
    const listHeight = Math.min(maxListHeight, Math.max(optionCount, 1) * OPTION_ROW_HEIGHT);
    return listHeight + searchRowHeight + MENU_CHROME_HEIGHT;
};

const getSelectedOptionTextColor = (theme) => {
    if (theme === "high-contrast") return "#000000";
    if (theme === "dark") return "#818cf8";
    return "#236DB4";
};

const getSelectedValueTextColor = (theme) => {
    if (theme === "high-contrast") return "#ffffff";
    if (theme === "dark") return "#f1f5f9";
    return "#111827";
};

const getSelectedOptionBackground = (theme, optionbackground) => {
    if (optionbackground) return optionbackground;
    if (theme === "dark") return "rgba(129, 140, 248, 0.15)";
    if (theme === "high-contrast") return "#ffff00";
    return "rgba(35, 109, 180, 0.08)";
};

// Tailwind's `sm` breakpoint. Below it a dropdown spans nearly the full width, so there is
// no room beside an option for a left/right tooltip -- it gets clipped or overlaps the menu.
const MOBILE_VIEWPORT_QUERY = "(max-width: 639px)";

const useIsMobileViewport = () => {
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
    });

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
        const update = () => setIsMobileViewport(mediaQuery.matches);
        update();
        mediaQuery.addEventListener?.("change", update);
        return () => mediaQuery.removeEventListener?.("change", update);
    }, []);

    return isMobileViewport;
};

// How long the menu stays open after the closeMenuOnSelectValue row is picked.
const CLOSE_AFTER_PICK_MS = 350;

const CheckboxOption = (props) => {
    const { langMode, theme } = useContext(AppContext);
    const isRtl = isRTL(langMode);
    const { isSelected, isMulti, data, label, selectProps } = props;
    const isMobileViewport = selectProps?.isMobileViewport;

    // Every option gets a hover tooltip, not just the ones clipped by the ellipsis — an
    // explicit `tooltip` on the option wins, otherwise the label itself is used.
    const tooltip = data?.tooltip ?? (typeof label === "string" ? label : undefined);
    const optionLabel = (
        <label className="min-w-0 flex-grow line-clamp-2 whitespace-normal break-words cursor-pointer">{props.label}</label>
    );

    return (
        <components.Option
            {...props}
            className={`!flex justify-between items-center px-3 py-2 cursor-pointer transition-all duration-150 ${isRtl ? 'text-[14px] text-right' : 'text-[15px]'}`}
        >
            {tooltip ? (
                <CommonTooltip
                    text={tooltip}
                    // On phones the tooltip stacks under the option row (CommonTooltip flips it
                    // above when there's no room below) instead of beside it.
                    position={isMobileViewport ? "bottom" : isRtl ? "left" : "right"}
                    className="min-w-0 flex-grow overflow-hidden"
                    disableDefaultDescription
                >
                    {optionLabel}
                </CommonTooltip>
            ) : (
                optionLabel
            )}
            {isMulti ? (
                <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    className="accent-[#236DB4]"
                    style={{
                        width: "15px",
                        height: "15px",
                        borderRadius: "4px",
                        cursor: "pointer",
                    }}
                />
            ) : (
                isSelected && (
                    <Check 
                        size={14} 
                        className={theme === "dark" ? "text-[#818cf8]" : theme === "high-contrast" ? "text-black" : "text-[#236DB4]"} 
                    />
                )
            )}
        </components.Option>
    );
};

const CustomMenuList = (props) => {
    const { children, selectProps } = props;
    const { langMode, searchQuery, setSearchQuery, onInputBlur, menuListHeader, hideSearch } = selectProps;
    const isRtl = isRTL(langMode);
    const inputRef = useRef(null);

    useEffect(() => {
        if (hideSearch) return undefined;
        const timer = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 10);
        return () => clearTimeout(timer);
    }, [hideSearch]);

    // Short fixed lists (e.g. the pagination page size) have nothing to search: render
    // the list alone so the menu is exactly as tall as its options.
    if (hideSearch && !menuListHeader) {
        return <components.MenuList {...props}>{children}</components.MenuList>;
    }

    return (
        <div className="flex flex-col max-h-full">
            {/* Caller-supplied row (e.g. an inline "create" action) pinned above the option
                list. Rendered here rather than by letting callers replace MenuList, so the
                search row and scroll behaviour below stay intact. Clicks are kept from
                reaching react-select, which would otherwise close the menu. */}
            {menuListHeader ? (
                <div
                    className="border-b border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    {menuListHeader}
                </div>
            ) : null}
            {!hideSearch && <div
                className="p-2 border-b border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="relative">
                    <Search
                        size={13}
                        className={`absolute ${isRtl ? "right-2.5" : "left-2.5"} top-1/2 -translate-y-1/2 text-slate-400`}
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        data-select-search="true"
                        value={searchQuery || ""}
                        onChange={(e) => setSearchQuery?.(e.target.value)}
                        onBlur={onInputBlur}
                        placeholder={translations[langMode]?.findAField ?? "Find a field..."}
                        className={`block w-full ${
                            isRtl ? "pr-8 pl-3" : "pl-8 pr-3"
                        } py-1.5 text-xs border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]`}
                        onKeyDown={(e) => {
                            if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter" && e.key !== "Escape") {
                                e.stopPropagation();
                            }
                        }}
                    />
                </div>
            </div>}
            <div className="overflow-y-auto flex-1">
                <components.MenuList {...props}>
                    {children}
                </components.MenuList>
            </div>
        </div>
    );
};

const DropdownIndicator = (props) => {
    const { menuIsOpen } = props.selectProps;
    return (
        <components.DropdownIndicator {...props}>
            <span className="text-[#64748b] hover:text-[#475569] transition-colors flex items-center justify-center">
                {menuIsOpen ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                )}
            </span>
        </components.DropdownIndicator>
    );
};

const CustomSingleValue = (props) => {
    return (
        <div style={props.getStyles('singleValue', props)} {...props.innerProps}>
            {props.children}
        </div>
    );
};

const CustomValueContainer = ({ children, ...props }) => {
    const { selectProps } = props;
    const hasValue = props.hasValue;
    const showPlaceholder = !hasValue && !selectProps.inputValue;
    const hasPlaceholder = React.Children.toArray(children).some(
        child => child && child.type && (child.type.name === 'Placeholder' || child.props?.id?.includes('placeholder'))
    );

    return (
        <components.ValueContainer {...props}>
            {showPlaceholder && !hasPlaceholder && (
                <components.Placeholder {...props} key="placeholder">
                    {selectProps.placeholder}
                </components.Placeholder>
            )}
            {children}
        </components.ValueContainer>
    );
};

const ReactSelector = ({
    maxMenuHeight = "250px",
    options,
    onChange,
    defaultValue,
    height = "42px",
    isDisabled,
    components = {},
    placeholder,
    isClearable,
    borderRadius,
    background,
    defaultMenuIsOpen,
    optionbackground,
    isMulti,
    fontSize = "15px",
    menuPortalTarget,
    menuPosition,
    overflow: _overflow,
    isSearchable = false,
    // Drops the "Find a field..." row (and the height reserved for it) from the menu.
    hideSearch = false,
    borderColor = "#00000066",
    styles: externalStyles = {},
    dataTestId = "react-selector",
    forceLtr = false,
    autoScrollMenuIntoView = false,
    selectedValuesMaxHeight,
    // react-select drops already-picked options from a multi menu by default, which leaves the
    // checkbox on every row permanently empty. Keep them listed so they show ticked.
    hideSelectedOptions = false,
    // Multi selects stay open after a pick; picking the option with this value (an "All" row)
    // closes the menu, since nothing else is left to choose.
    closeMenuOnSelectValue,
    onMenuOpen,
    onMenuClose,
    // Opt-in controlled open state. Left undefined the selector manages the menu itself
    // (the default for every call site); pass it when the caller needs the menu to stay
    // open through an interaction of its own, e.g. typing in a menuListHeader input.
    menuIsOpen: menuIsOpenProp,
    ...rest
}) => {
    const { langMode, theme } = useContext(AppContext);
    const isRtl = !forceLtr && isRTL(langMode);
    // Resolved once here and handed to the options through selectProps, so a long list
    // doesn't register one media-query listener per rendered option.
    const isMobileViewport = useIsMobileViewport();
    const selectorRef = useRef(null);
    const closeAfterPickRef = useRef(null);
    useEffect(() => () => clearTimeout(closeAfterPickRef.current), []);
    const clipParent = selectorRef.current ? getClipParent(selectorRef.current) : null;
    const fallbackPortalTarget =
        typeof document !== "undefined" && document.body && clipParent ? document.body : undefined;
    const resolvedMenuPortalTarget = getSafeMenuPortalTarget(menuPortalTarget) ?? getSafeMenuPortalTarget(fallbackPortalTarget);
    // react-select positions a portaled menu with `position: absolute` by default,
    // which is measured against the document rather than the viewport -- when the
    // control sits inside a modal deep in the page, that can place the portaled
    // menu far enough down to expand `document.body`'s scrollable height, showing
    // up as blank space / a broken layout on the page behind the modal once the
    // menu (or even just the modal) is open. `menuPosition="fixed"` measures
    // against the viewport instead, which is what portaled menus need everywhere.
    // Only defaults this when a portal target is actually in play, and never
    // overrides a caller that explicitly passed its own menuPosition.
    const resolvedMenuPosition = menuPosition ?? (resolvedMenuPortalTarget ? "fixed" : undefined);
    // react-select only flips the menu above the control once neither its "fits below"
    // nor its "fits below if constrained" branch matches, and both compare against
    // minMenuHeight (default 140). With menuPosition="fixed" that space is measured
    // against the viewport, so inside a shorter modal it always looks like there's room
    // below and the menu never flips. Reporting the menu's true height fixes the choice.
    const searchRowHeight = hideSearch ? 0 : MENU_SEARCH_ROW_HEIGHT;
    const menuTotalHeight = toPixelHeight(maxMenuHeight) + searchRowHeight;
    const [isMenuOpen, setIsMenuOpen] = useState(Boolean(defaultMenuIsOpen));
    const [searchQuery, setSearchQuery] = useState("");
    const [resolvedPlacement, setResolvedPlacement] = useState(null);
    // Whether the menu is actually showing. A controlled caller wins over the internal
    // state, so the placement/outside-click/Escape handling below stays in step with what
    // is on screen (the internal state can be pushed to false by a focus change the
    // caller deliberately ignores, e.g. focus moving into a menuListHeader input).
    const menuOpen = menuIsOpenProp ?? isMenuOpen;

    const requestedPlacement = autoScrollMenuIntoView ? "bottom" : rest.menuPlacement;

    useEffect(() => {
        if (!menuOpen) {
            setSearchQuery("");
        }
    }, [menuOpen]);

    const filteredOptions = useMemo(() => {
        if (!searchQuery) return options;
        const query = searchQuery.toLowerCase();
        const matchesQuery = (opt) => String(opt?.label || "").toLowerCase().includes(query);
        return (options || []).reduce((acc, opt) => {
            // Grouped options (`{ label, options: [...] }`) are filtered by their children, so
            // searching narrows the entries inside each group instead of dropping every group
            // because the group's own label doesn't match.
            if (Array.isArray(opt?.options)) {
                const groupOptions = opt.options.filter(matchesQuery);
                if (groupOptions.length) acc.push({ ...opt, options: groupOptions });
                return acc;
            }
            if (matchesQuery(opt)) acc.push(opt);
            return acc;
        }, []);
    }, [options, searchQuery]);

    // Groups contribute their children plus their own heading row, so the height estimate
    // below reflects what a grouped menu actually renders.
    const visibleOptionCount = useMemo(
        () => (filteredOptions || []).reduce(
            (total, opt) => total + (Array.isArray(opt?.options) ? opt.options.length + 1 : 1),
            0
        ),
        [filteredOptions]
    );

    const estimatedMenuHeight = getEstimatedMenuHeight(visibleOptionCount, menuTotalHeight, searchRowHeight);

    // Every placement is a preference, not a command: the menu opens on the preferred side
    // when it fits there, otherwise on the opposite side, and when neither side can hold it
    // on whichever has more room. Measured against the clipping box intersected with the
    // viewport — react-select's own "auto" measures the viewport once menuPosition is
    // "fixed", so a menu that clears the window but not the modal it sits in still opens
    // downward and spills, while a portaled menu can't paint past the window either.
    const resolvePlacement = (preferred) => {
        const controlElement = selectorRef.current;
        if (!controlElement) return null;

        const clipParent = getClipParent(controlElement);
        const clipRect = clipParent?.getBoundingClientRect();
        const controlRect = controlElement.getBoundingClientRect();
        const bounds = {
            top: Math.max(0, clipRect?.top ?? 0),
            bottom: Math.min(window.innerHeight, clipRect?.bottom ?? window.innerHeight),
        };
        const spaceBelow = bounds.bottom - controlRect.bottom;
        const spaceAbove = controlRect.top - bounds.top;
        const fitsBelow = spaceBelow >= estimatedMenuHeight;
        const fitsAbove = spaceAbove >= estimatedMenuHeight;

        if (preferred === "top") {
            if (fitsAbove) return "top";
            if (fitsBelow) return "bottom";
        } else {
            if (fitsBelow) return "bottom";
            if (fitsAbove) return "top";
        }

        return spaceAbove > spaceBelow ? "top" : "bottom";
    };

    const handleMenuOpen = () => {
        setIsMenuOpen(true);
        // autoScrollMenuIntoView scrolls the page to make room below instead of flipping.
        if (!autoScrollMenuIntoView) {
            setResolvedPlacement(resolvePlacement(requestedPlacement));
        }
        onMenuOpen?.();
        if (!autoScrollMenuIntoView) return;

        window.requestAnimationFrame(() => {
            const controlElement = selectorRef.current;
            const scrollParent = getScrollParent(controlElement);
            if (!controlElement || !scrollParent) return;

            const controlRect = controlElement.getBoundingClientRect();
            const parentRect =
                scrollParent === document.scrollingElement || scrollParent === document.documentElement
                    ? { top: 0, bottom: window.innerHeight }
                    : scrollParent.getBoundingClientRect();
            const menuHeight = Math.min(menuTotalHeight, 250);
            const desiredBottom = controlRect.bottom + menuHeight + 18;
            const overflowBottom = desiredBottom - parentRect.bottom;

            if (overflowBottom > 0) {
                scrollParent.scrollBy({
                    top: overflowBottom,
                    behavior: "smooth",
                });
            }
        });
    };

    // The control keeps moving while the menu is open — page or modal scrolling, a resize,
    // the list shrinking as the search filters it — so re-check which side fits instead of
    // deciding once at open time. Layout effect so a menu opened without handleMenuOpen
    // (defaultMenuIsOpen) never paints on the wrong side first.
    useLayoutEffect(() => {
        if (!menuOpen || autoScrollMenuIntoView) return;

        const update = () => setResolvedPlacement((prev) => resolvePlacement(requestedPlacement) ?? prev);
        update();

        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuOpen, autoScrollMenuIntoView, requestedPlacement, estimatedMenuHeight]);

    const handleMenuClose = () => {
        // No-op to allow direct close handling inside onBlur, click-outside, and Escape key listeners.
    };

    const handleBlur = (event) => {
        const relatedTarget = event.relatedTarget;
        if (relatedTarget && relatedTarget.getAttribute("data-select-search") === "true") {
            return;
        }
        setIsMenuOpen(false);
        onMenuClose?.();
    };

    const handleInputBlur = (event) => {
        const relatedTarget = event.relatedTarget;
        if (!relatedTarget) return;
        if (selectorRef.current && !selectorRef.current.contains(relatedTarget) && !relatedTarget?.closest?.(".select__menu")) {
            setIsMenuOpen(false);
            onMenuClose?.();
        }
    };

    useEffect(() => {
        if (!menuOpen) return;

        const handleDocumentClick = (event) => {
            if (selectorRef.current && selectorRef.current.contains(event.target) && !event.target.closest(".select__menu")) {
                setIsMenuOpen(false);
                onMenuClose?.();
                return;
            }
            if (selectorRef.current && !selectorRef.current.contains(event.target) && !event.target.closest(".select__menu")) {
                setIsMenuOpen(false);
                onMenuClose?.();
            }
        };

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsMenuOpen(false);
                onMenuClose?.();
            }
        };

        let bound = false;
        const timer = setTimeout(() => {
            document.addEventListener("mousedown", handleDocumentClick);
            bound = true;
        }, 0);

        document.addEventListener("keydown", handleEscape);
        return () => {
            clearTimeout(timer);
            if (bound) {
                document.removeEventListener("mousedown", handleDocumentClick);
            }
            document.removeEventListener("keydown", handleEscape);
        };
    }, [menuOpen, onMenuClose]);

    // react-select only opens its menu on Enter once it's already open (to confirm
    // the focused option); pressing Enter while closed is a no-op upstream. Tabbing
    // to the control and pressing Enter should open it like a native <select>.
    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !menuOpen && !isDisabled) {
            event.preventDefault();
            setIsMenuOpen(true);
            onMenuOpen?.();
        }
    };

    const resolvedComponents = typeof components === "function"
        ? { Placeholder: components }
        : components;


    const mergeStyle = (key, ...args) => {
        const internal = internalStyles[key];
        const external = externalStyles[key];
        const base = typeof internal === 'function' ? internal(...args) : internal;
        const overrides = typeof external === 'function' ? external(...args) : external;
        return overrides ? { ...base, ...overrides } : base;
    };

    const internalStyles = {
        control: (baseStyles, state) => {
            let borderCol = '#cbd5e1';
            let bgCol = '#ffffff';
            let shadowCol = 'rgba(35, 109, 180, 0.15)';
            let focusBorderCol = '#236DB4';
            let hoverBorderCol = '#94a3b8';

            if (theme === "dark") {
                borderCol = '#475569';
                bgCol = '#1e293b';
                focusBorderCol = '#818cf8';
                shadowCol = 'rgba(129, 140, 248, 0.25)';
                hoverBorderCol = '#475569';
            } else if (theme === "high-contrast") {
                borderCol = '#ffffff';
                bgCol = '#000000';
                focusBorderCol = '#ffff00';
                shadowCol = 'rgba(255, 255, 0, 0.4)';
                hoverBorderCol = '#ffff00';
            }

            if (state.isDisabled) {
                borderCol = theme === "dark" ? '#334155' : '#e2e8f0';
                bgCol = theme === "dark" ? '#1e293b' : '#f8fafc';
            }

            return {
                ...baseStyles,
                minHeight: height,
                height: isMulti ? "auto" : height,
                display: "flex",
                alignItems: "center",
                borderColor: state.isFocused || state.selectProps.menuIsOpen
                    ? focusBorderCol
                    : borderCol,
                boxShadow: state.isFocused || state.selectProps.menuIsOpen
                    ? `0 0 0 3px ${shadowCol}`
                    : '0 1px 2px rgba(0, 0, 0, 0.04)',
                borderRadius: borderRadius || "10px",
                background: background || bgCol,
                fontSize,
                fontFamily: 'inherit',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: hoverBorderCol },
                overflow: "hidden",
                overflowX: "hidden",
                direction: isRtl ? "rtl" : "ltr",
                cursor: state.isDisabled ? "not-allowed" : "pointer",
            };
        },
        valueContainer: (base) => ({
            ...base,
            height: isMulti ? "auto" : height,
            maxHeight: isMulti && selectedValuesMaxHeight ? selectedValuesMaxHeight : undefined,
            display: isMulti ? "flex" : base.display,
            flexWrap: isMulti ? "wrap" : base.flexWrap,
            alignItems: "center",
            justifyContent: "flex-start",
            gap: isMulti ? "4px" : "0",
            padding: isMulti ? "4px 8px" : "0 8px",
            overflowY: isMulti && selectedValuesMaxHeight ? "auto" : "hidden",
            overflowX: "hidden",
            minWidth: 0,
            direction: isRtl ? "rtl" : "ltr",
            boxSizing: "border-box",
        }),
        inputContainer: (base) => ({
            ...base,
            minWidth: 0,
            overflow: "hidden",
        }),
        input: (base) => ({
            ...base,
            opacity: 0,
            width: 0,
            height: 0,
            margin: 0,
            padding: 0,
            overflow: "hidden",
            pointerEvents: "none",
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height,
            alignItems: "center",
        }),
        dropdownIndicator: (base) => ({
            ...base,
            display: "flex",
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: isRtl ? "4px" : "12px",
            paddingLeft: isRtl ? "12px" : "4px",
        }),
        multiValue: (base) => {
            let bgCol = '#f1f5f9';
            let borderCol = 'transparent';
            if (theme === "dark") {
                bgCol = '#334155';
            } else if (theme === "high-contrast") {
                bgCol = '#000000';
                borderCol = '#ffffff';
            }
            return {
                ...base,
                backgroundColor: bgCol,
                border: borderCol !== 'transparent' ? `1px solid ${borderCol}` : undefined,
                margin: 0,
                maxWidth: "calc(100% - 2.5rem)",
                flexShrink: 1,
                minWidth: 0,
            };
        },
        multiValueLabel: (base) => {
            let textCol = theme === 'high-contrast' ? '#ffffff' : (theme === 'dark' ? '#f1f5f9' : '#334155');
            return {
                ...base,
                color: textCol,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingRight: "4px",
                paddingLeft: isRtl ? "4px" : undefined,
            };
        },
        multiValueRemove: (base) => {
            let textCol = theme === 'high-contrast' ? '#ffffff' : (theme === 'dark' ? '#94a3b8' : '#64748b');
            return {
                ...base,
                color: textCol,
                flexShrink: 0,
                '&:hover': {
                    backgroundColor: theme === 'high-contrast' ? '#ffff00' : (theme === 'dark' ? '#ef4444' : '#fee2e2'),
                    color: theme === 'high-contrast' ? '#000000' : (theme === 'dark' ? '#ffffff' : '#ef4444'),
                }
            };
        },
        menu: (baseStyles) => {
            let bgCol = '#ffffff';
            let borderCol = '#cbd5e1';
            if (theme === "dark") {
                bgCol = '#1e293b';
                borderCol = '#475569';
            } else if (theme === "high-contrast") {
                bgCol = '#000000';
                borderCol = '#ffffff';
            }
            return {
                ...baseStyles,
                minWidth: "100%",
                marginTop: "4px",
                marginBottom: "4px",
                border: `1px solid ${borderCol}`,
                zIndex: 999,
                borderRadius: "10px",
                boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.12), 0 4px 6px -2px rgba(0, 0, 0, 0.04)",
                overflow: 'hidden',
                backgroundColor: bgCol,
            };
        },
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menuList: (baseStyles) => ({
            ...baseStyles,
            maxHeight: maxMenuHeight,
            paddingTop: "6px",
            paddingBottom: "6px",
            overflowX: "hidden",
            direction: isRtl ? "rtl" : "ltr",
        }),
        option: (provided, state) => {
            let bgCol = 'transparent';
            let textCol = '#334155';

            if (theme === "dark") {
                textCol = '#f1f5f9';
                bgCol = state.isSelected
                    ? getSelectedOptionBackground(theme, optionbackground)
                    : state.isFocused
                        ? '#334155'
                        : 'transparent';
            } else if (theme === "high-contrast") {
                textCol = '#ffffff';
                bgCol = state.isSelected
                    ? getSelectedOptionBackground(theme, optionbackground)
                    : state.isFocused
                        ? '#333333'
                        : 'transparent';
            } else {
                bgCol = state.isSelected
                    ? getSelectedOptionBackground(theme, optionbackground)
                    : state.isFocused
                        ? '#f8fafc'
                        : 'transparent';
            }

            let selTextCol = state.isSelected ? getSelectedOptionTextColor(theme) : textCol;

            return {
                ...provided,
                backgroundColor: bgCol,
                color: selTextCol,
                fontSize,
                padding: '8px 12px',
                cursor: 'pointer',
                textAlign: isRtl ? "right" : "left",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
            };
        },
        placeholder: (provided, state) => {
            let textCol = theme === 'high-contrast' ? '#bbbbbb' : (theme === 'dark' ? '#64748b' : '#94a3b8');
            return {
                ...provided,
                fontSize,
                color: textCol,
                textAlign: isRtl ? "right" : "left",
                direction: isRtl ? "rtl" : "ltr",
                display: "block",
                lineHeight: "20px",
                margin: 0,
                minWidth: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                position: isMulti ? "absolute" : provided.position,
                left: isMulti ? "14px" : provided.left,
                right: isMulti ? "14px" : provided.right,
                opacity: state.isDisabled ? 0.5 : 1,
            };
        },
        singleValue: (provided, state) => {
            let textCol = state.hasValue
                ? getSelectedValueTextColor(theme)
                : (theme === 'high-contrast' ? '#ffffff' : (theme === 'dark' ? '#f1f5f9' : '#334155'));
            return {
                ...provided,
                fontSize,
                fontFamily: 'inherit',
                color: textCol,
                fontWeight: 500,
                padding: 0,
                minWidth: isMulti ? "2px" : provided.minWidth,
                flex: isMulti ? "1 1 auto" : provided.flex,
                direction: isRtl ? "rtl" : "ltr",
                display: "block",
                lineHeight: "20px",
                margin: 0,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                position: isMulti ? "absolute" : provided.position,
                left: isMulti ? "14px" : provided.left,
                right: isMulti ? "14px" : provided.right,
                opacity: state.isDisabled ? 0.5 : 1,
            };
        },
    };

    const mergedStyles = {};
    Object.keys(internalStyles).forEach((key) => {
        const internal = internalStyles[key];
        const external = externalStyles[key];
        if (typeof internal !== 'function') {
            mergedStyles[key] = external ? { ...internal, ...(typeof external === 'function' ? external({}) : external) } : internal;
        } else {
            mergedStyles[key] = (...args) => mergeStyle(key, ...args);
        }
    });
    Object.keys(externalStyles).forEach((key) => {
        if (!mergedStyles[key]) {
            mergedStyles[key] = externalStyles[key];
        }
    });

    const handleChange = (newValue, actionMeta) => {
        onChange?.(newValue, actionMeta);
        const pickedCloseOption =
            isMulti &&
            closeMenuOnSelectValue !== undefined &&
            actionMeta?.action === "select-option" &&
            actionMeta?.option?.value === closeMenuOnSelectValue;
        if (!isMulti) {
            setIsMenuOpen(false);
            onMenuClose?.();
        } else if (pickedCloseOption) {
            // Leave the menu up for a beat so the ticked "All" row is seen before it closes.
            clearTimeout(closeAfterPickRef.current);
            closeAfterPickRef.current = setTimeout(() => {
                setIsMenuOpen(false);
                onMenuClose?.();
            }, CLOSE_AFTER_PICK_MS);
        }
    };

    return (
        <div
            ref={selectorRef}
            className={selectedValuesMaxHeight ? "react-selector-selected-scroll" : undefined}
            onKeyDown={handleKeyDown}
        >
        <Select
            {...rest}
            isMulti={isMulti}
            hideSelectedOptions={hideSelectedOptions}
            menuIsOpen={menuOpen}
            isClearable={isClearable}
            className="basic-single"
            classNamePrefix="select"
            
            options={filteredOptions}
            isDisabled={isDisabled}
            defaultValue={defaultValue}
            components={{
                ...resolvedComponents,
                Option: CheckboxOption,
                MenuList: CustomMenuList,
                DropdownIndicator,
                IndicatorSeparator: () => null,
                SingleValue: CustomSingleValue,
                ValueContainer: CustomValueContainer,
            }}
            placeholder={placeholder}
            onChange={handleChange}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            isSearchable={false}
            langMode={langMode}
            isMobileViewport={isMobileViewport}
            hideSearch={hideSearch}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onBlur={handleBlur}
            onInputBlur={handleInputBlur}
            menuPortalTarget={resolvedMenuPortalTarget}
            menuPosition={resolvedMenuPosition}
            maxMenuHeight={menuTotalHeight}
            // react-select runs its own space check on top of ours; feeding it the height
            // the menu will actually need keeps its fallback from flipping to the side we
            // just ruled out.
            minMenuHeight={estimatedMenuHeight}
            menuPlacement={resolvedPlacement || requestedPlacement || "auto"}
            styles={mergedStyles}
            data-testid={dataTestId}
            inputId={dataTestId}
            isRtl={isRtl}
        />
        </div>
    );
};

export default ReactSelector;
